// /oversight/tracker?project=<id>&from&to (architecture sprint-11 §9.3). Missing entries for one project
// a Supervisor reviews: rows are its members, columns its working days, hours on that project only.
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EmptyState } from '@/components/EmptyState';
import { Page } from '@/components/Page';
import { PageHeader } from '@/components/PageHeader';
import { ProjectScopeSelect, resolveScope } from '@/components/ProjectScopeSelect';
import { RangePicker, type RangePreset } from '@/components/RangePicker';
import { Skeleton } from '@/components/ui/skeleton';
import { useMe } from '@/hooks/use-me';
import { useOversightProjects, useScopedTracker } from '@/hooks/use-oversight';
import { isValidReportRange, type DateRange } from '@/lib/report-model';
import { defaultTrackerRange, oversightRecordsLinkFor, TRACKER_PRESET_LABELS, trackerPresetRange } from '@/lib/tracker-model';
import { useToday } from '@/lib/use-now';
import { TrackerGrid } from '@/pages/admin/tracker/TrackerGrid';
import type { OversightProject } from '@/types/settings';
import { formatWorkweek } from '@shared/workweek';

export function OversightTrackerPage(): JSX.Element {
  const { data: me } = useMe();
  const projects = useOversightProjects();
  if (!me || !projects.data)
    return (
      <Page width="6xl">
        <Skeleton className="h-96 w-full" />
      </Page>
    );
  // Sprint 13 (§11): shift projects use Attendance instead of Missing entries.
  const choices = projects.data.filter((p) => p.caps.viewTeam && p.shiftEnabled !== true);
  if (choices.length === 0)
    return (
      <Page width="6xl">
        <PageHeader title="Missing entries" />
        <EmptyState title="No projects to review." />
      </Page>
    );
  return <TrackerView tz={me.timezone} choices={choices} />;
}

function TrackerView({ tz, choices }: { tz: string; choices: OversightProject[] }): JSX.Element {
  const today = useToday(tz);
  const [params, setParams] = useSearchParams();
  const rawFrom = params.get('from');
  const rawTo = params.get('to');
  const valid = isValidReportRange(rawFrom, rawTo);
  const range: DateRange = valid && rawFrom && rawTo ? { from: rawFrom, to: rawTo } : defaultTrackerRange(today);
  const { scope, valid: scopeValid } = resolveScope(params.get('project'), choices, false);
  const project = choices.find((p) => p.id === scope) ?? null;

  useEffect(() => {
    if ((!valid || !scopeValid) && project) setParams({ project: project.id, from: range.from, to: range.to }, { replace: true });
  }, [valid, scopeValid, project, range.from, range.to, setParams]);

  const tracker = useScopedTracker(project?.id ?? null, range.from, range.to, project !== null);
  const presets: RangePreset[] = TRACKER_PRESET_LABELS.map((p) => ({ key: p.key, label: p.label, range: trackerPresetRange(p.key, today) }));

  return (
    <Page width="6xl">
      <PageHeader title="Missing entries" hint="Working days with no time logged on this project are highlighted. Select a day to open it in Records." />
      <div className="mb-4">
        <ProjectScopeSelect
          id="oversight-tracker-project"
          value={project?.id ?? ''}
          projects={choices}
          allowAll={false}
          onChange={(id) => setParams({ project: id, from: range.from, to: range.to })}
        />
      </div>
      <RangePicker from={range.from} to={range.to} presets={presets} onChange={(r) => setParams({ project: project?.id ?? '', from: r.from, to: r.to })} />
      {tracker.data && project && (
        <p className="mb-3 text-sm text-muted-foreground">
          Members of {project.name} · {formatWorkweek(tracker.data.workweekDays)}
        </p>
      )}
      {!tracker.data || !project ? (
        <Skeleton className="h-72 w-full rounded-xl" />
      ) : tracker.data.dates.length === 0 ? (
        <EmptyState title="No working days in this range." />
      ) : (
        <TrackerGrid tracker={tracker.data} linkFor={(userId, date) => oversightRecordsLinkFor(project.id, userId, date)} />
      )}
    </Page>
  );
}
